import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Horizontal } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { LabelM, LabelS } from 'atoms/typography';
import Avatar from 'components/user/Avatar';
import { Avatar_user } from 'components/user/Avatar/__generated__/index.graphql';
import { rivals } from 'lib/glossary';
import { Color, HexColor } from 'style/types';

import { Arrows } from './Arrows';
import { DivisionRanking, SeasonLearderboardRow } from './SeasonLeaderboardRow';
import { addAvatarToRanking_user } from './__generated__/index.graphql';

const pseudoElementsStyle = css`
  content: '';
  position: absolute;
  width: 100%;
  z-index: -1;
`;

export const RootTable = styled.table`
  --table-header-height: var(--quadruple-unit);
  --table-row-height: calc(8 * var(--unit));
  --table-separator-row-height: 52px;
  --gradient-promoted-color: #002423;
  --promoted-color: #00413f;
  --relegated-color: #af2604;
  --promoted-title-color: var(--c-rivals-friendlies);
  --relegated-title-color: var(--c-rivals-loss);

  isolation: isolate;
  position: relative;
  border-spacing: 0;

  &.showPromoted::before {
    ${pseudoElementsStyle}
    height: calc(
      var(--promoted-nb) * var(--table-row-height) +
        var(--table-separator-row-height)
    );
    background: linear-gradient(
      180deg,
      var(--promoted-color) 10%,
      var(--gradient-promoted-color) 81%,
      var(--c-neutral-100) 100%
    );
  }

  &.showRelegated::after {
    ${pseudoElementsStyle}
    bottom: 0;
    height: calc(
      var(--relegated-nb) * var(--table-row-height) +
        var(--table-separator-row-height)
    );
    background: linear-gradient(
      180deg,
      var(--c-neutral-100) 0%,
      #451205 32%,
      var(--relegated-color) 90%
    );
  }
`;
export const TableHeaderRow = styled.tr<{
  bgColor?: HexColor;
}>`
  color: var(--c-neutral-600);
  height: var(--table-header-height);
  background: ${({ bgColor }) => bgColor || 'var(--c-neutral-100)'};
  position: sticky;
  z-index: 1;
  top: var(--current-stack-height);

  & > th:not(:first-child) {
    vertical-align: middle;
    align-items: center;
  }
`;
export const DivisionSeparator = styled.td`
  padding: var(--intermediate-unit);
  height: var(--table-separator-row-height);
`;

type DivisionRankingWithAvatar = DivisionRanking & {
  avatar: ReactNode;
};

type Props = {
  promotedSectionTitle?: string;
  relegatedSectionTitle?: string;
  promotedRankings: DivisionRankingWithAvatar[];
  unrelegatedRankings: DivisionRankingWithAvatar[];
  relegatedRankings: DivisionRankingWithAvatar[];
  featuredManagerId?: string;
  selectedUserSlug?: string;
  onRowClick?: (userSlug: string) => void;
  hideHeader?: boolean;
  additionalStyles?: CSSProperties;
};

export const addAvatarToRanking = (
  ranking: DivisionRanking & { manager: { user: Avatar_user } }
): DivisionRankingWithAvatar => {
  return {
    ...ranking,
    avatar: <Avatar user={ranking.manager.user} size={4} rounded />,
  };
};
addAvatarToRanking.fragments = {
  user: gql`
    fragment addAvatarToRanking_user on User {
      id
      slug
      ...Avatar_user
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<addAvatarToRanking_user>,
};

export const SeasonLeaderboardList = ({
  promotedSectionTitle,
  relegatedSectionTitle,
  promotedRankings,
  unrelegatedRankings,
  relegatedRankings,
  featuredManagerId,
  selectedUserSlug,
  onRowClick,
  hideHeader,
  additionalStyles,
}: Props) => {
  const showPromoted = promotedRankings.length > 0;
  const showRelegated = relegatedRankings.length > 0;

  return (
    <RootTable
      className={classNames({ showPromoted, showRelegated })}
      style={
        {
          '--promoted-nb': promotedRankings.length,
          '--relegated-nb': relegatedRankings.length,
          ...additionalStyles,
        } as CSSProperties
      }
    >
      {!hideHeader && (
        <thead>
          <TableHeaderRow>
            <th>
              <Tooltip
                title={
                  <LabelS>
                    <FormattedMessage {...rivals.leaderboardPosition} />
                  </LabelS>
                }
              >
                <LabelS as="span">
                  <FormattedMessage
                    {...rivals.leaderboardPositionAbbreviated}
                  />
                </LabelS>
              </Tooltip>
            </th>
            <th>
              <LabelS as="span">
                <FormattedMessage
                  id="SeasonLeaderboard.tableHeader.manager"
                  defaultMessage="Manager"
                />
              </LabelS>
            </th>
            <th>
              <Tooltip
                title={
                  <LabelS>
                    <FormattedMessage
                      id="SeasonLeaderboard.tableHeader.scoreDifference"
                      defaultMessage="Score difference"
                    />
                  </LabelS>
                }
              >
                <LabelS as="span">
                  <FormattedMessage
                    id="SeasonLeaderboard.tableHeader.abbreviatedScoreDifference"
                    defaultMessage="SD"
                  />
                </LabelS>
              </Tooltip>
            </th>
            <th>
              <LabelS as="span">
                <FormattedMessage {...rivals.rep} />
              </LabelS>
            </th>
          </TableHeaderRow>
        </thead>
      )}
      <tbody>
        {promotedRankings.map(ranking => {
          return (
            <SeasonLearderboardRow
              key={ranking.id}
              divisionRanking={ranking}
              featuredManagerId={featuredManagerId}
              selected={selectedUserSlug === ranking.manager.user.slug}
              avatar={ranking.avatar}
              onRowClick={onRowClick}
              promoted
            />
          );
        })}
        {showPromoted && (
          <tr>
            <DivisionSeparator colSpan={4}>
              <Horizontal center gap={4}>
                <Arrows color={'var(--promoted-title-color)' as Color} />
                <LabelM bold as="span" color="var(--promoted-title-color)">
                  {promotedSectionTitle}
                </LabelM>
                <Arrows color={'var(--promoted-title-color)' as Color} />
              </Horizontal>
            </DivisionSeparator>
          </tr>
        )}
        {unrelegatedRankings.map(ranking => {
          return (
            <SeasonLearderboardRow
              key={ranking.id}
              divisionRanking={ranking}
              featuredManagerId={featuredManagerId}
              selected={selectedUserSlug === ranking.manager.user.slug}
              avatar={ranking.avatar}
              onRowClick={onRowClick}
            />
          );
        })}
        {relegatedRankings.length > 0 && (
          <tr>
            <DivisionSeparator colSpan={4}>
              <Horizontal center gap={4}>
                <Arrows
                  reverse
                  color={'var(--relegated-title-color)' as Color}
                />
                <LabelM bold as="span" color="var(--relegated-title-color)">
                  {relegatedSectionTitle}
                </LabelM>
                <Arrows
                  reverse
                  color={'var(--relegated-title-color)' as Color}
                />
              </Horizontal>
            </DivisionSeparator>
          </tr>
        )}
        {relegatedRankings.map(ranking => {
          return (
            <SeasonLearderboardRow
              key={ranking.id}
              divisionRanking={ranking}
              featuredManagerId={featuredManagerId}
              selected={selectedUserSlug === ranking.manager.user.slug}
              avatar={ranking.avatar}
              onRowClick={onRowClick}
              relegated
            />
          );
        })}
      </tbody>
    </RootTable>
  );
};
